import { GridColDef } from "@mui/x-data-grid-premium";
import {
  Groups,
  Person,
  Public,
  PriorityHigh,
  SupportAgent,
  HowToReg
} from "@mui/icons-material";
import { Box } from "@mui/material";
import { useMemo } from "react";
import EventNote from "@mui/icons-material/EventNote";
import { CountrySummary } from "../../../../../api/Api";
import { CountryFlag } from "../../../../../components/countryFlag";
import {
  SIGN_OFF_ORDER,
  UNASSIGNED,
  dateColumn,
  normalizeProgress,
  normalizeSignOff,
  renderPriorityCell,
  renderProgressCell,
  renderSignOffCell
} from "../../../../../components/datagrid/overviewCells";

// Distinct Market Access handlers present in the current rows, used as the dropdown
// options for the filter. Rows without a handler collapse to a single "Unassigned"
// option so they can be filtered for explicitly.
const useHandlerOptions = (rows: CountrySummary[]) =>
  useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      set.add(row.marketAccessHandler || UNASSIGNED);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

// The seven lifecycles a country carries, same set as a counterparty.
const PROGRESS_COLUMNS: {
  field: string;
  headerName: string;
  key: keyof NonNullable<CountrySummary["progress"]>;
}[] = [
  {
    field: "progress_marketAccess",
    headerName: "Market Access",
    key: "marketAccess"
  },
  { field: "progress_compliance", headerName: "Compliance", key: "compliance" },
  {
    field: "progress_creditRisk",
    headerName: "Credit Risk",
    key: "creditRisk"
  },
  { field: "progress_legal", headerName: "Legal", key: "legal" },
  { field: "progress_risk", headerName: "Risk", key: "risk" },

  { field: "progress_treasury", headerName: "Treasury", key: "treasury" },
  { field: "progress_finance", headerName: "Finance", key: "finance" },
  { field: "progress_tech", headerName: "Tech", key: "tech" }
];

export const useCountryColumns = (
  rows: CountrySummary[] = [],
  showMarketAccessHandler = false
): GridColDef<CountrySummary>[] => {
  const handlerOptions = useHandlerOptions(rows);

  return useMemo(() => {
    const columns: GridColDef<CountrySummary>[] = [
      {
        // Board comment #44: this column header reads "Country", not "Counterparty".
        field: "countryName",
        headerName: "Country",
        width: 300,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Public fontSize="small" />
            Country
          </Box>
        ),
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CountryFlag countryName={params.value} width={20} />
            {params.value}
          </Box>
        )
      },
      {
        field: "tradingTeam",
        headerName: "Trading Team",
        width: 200,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Groups fontSize="small" />
            Trading Team
          </Box>
        )
      },
      {
        field: "contactPersonTradingTeam",
        headerName: "MFT Contact Person",
        width: 250,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Person fontSize="small" />
            MFT Contact Person
          </Box>
        )
      },
      dateColumn<CountrySummary>(
        "requestedAt",
        "Requested",
        (row) => row.requestedAt,
        <EventNote fontSize="small" />
      ),
      // Only the global role (Admin / Market Access) sees who from MA is handling each row.
      ...(showMarketAccessHandler
        ? [
            {
              field: "marketAccessHandler",
              headerName: "MA Handler",
              width: 220,
              sortable: true,
              filterable: true,
              type: "singleSelect",
              valueOptions: handlerOptions,
              valueGetter: (_value, row) =>
                row.marketAccessHandler || UNASSIGNED,
              renderHeader: () => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SupportAgent fontSize="small" />
                  MA Handler
                </Box>
              )
            } as GridColDef<CountrySummary>
          ]
        : []),
      {
        field: "urgent",
        headerName: "Priority",
        width: 120,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PriorityHigh fontSize="small" />
            Priority
          </Box>
        ),
        renderCell: (params) => renderPriorityCell(params.value)
      },
      {
        field: "signOffStatus",
        headerName: "Sign Off",
        width: 150,
        sortable: true,
        filterable: true,
        type: "singleSelect",
        valueOptions: SIGN_OFF_ORDER,
        valueGetter: (_value, row) => normalizeSignOff(row.signOffStatus),
        sortComparator: (a, b) =>
          SIGN_OFF_ORDER.indexOf(a) - SIGN_OFF_ORDER.indexOf(b),
        renderCell: (params) => renderSignOffCell(params.value),
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HowToReg fontSize="small" />
            Sign Off
          </Box>
        )
      },
      ...PROGRESS_COLUMNS.map(
        ({ field, headerName, key }): GridColDef<CountrySummary> => ({
          field,
          headerName,
          width: 150,
          sortable: true,
          filterable: true,
          valueGetter: (_value, row) => normalizeProgress(row.progress?.[key]),
          renderCell: (params) => renderProgressCell(params.value)
        })
      )
    ];
    return columns;
  }, [handlerOptions, showMarketAccessHandler]);
};
